/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { queryCollection } from '@theme/collection-plp-api';
import { getElevateClient } from '@theme/elevate-client';
import { resolveUserContext } from '@theme/elevate-products';
import {
  getPageURL,
  getSelectedFacets,
  getSelectedPage,
  getSelectedSort,
  replacePageURL,
  updateFacetURL,
  updatePageURL,
  updateRangeFacetURL,
  updateSortURL,
  syncRobotsMeta,
} from '@theme/collection-plp-url';
import { getScrollContainer } from '@theme/scroll-container';

const entryScript = document.querySelector('script[data-collection-plp-view-url]');
const viewModuleUrl = entryScript?.dataset.collectionPlpViewUrl || './collection-plp-view.js';
const { collectionPlpViewMethods, normalizeCollectionPlpGridView } = await import(viewModuleUrl);

const RESTORE_STORAGE_PREFIX = 'collection-plp-restore';

// Elevate rejects the query outright above this limit ("The value must be less than or equal
// to 600"), so deep pages are collected in chunks rather than truncated to fit one request.
const ELEVATE_MAX_LIMIT = 600;

// `getScrollContainer()` assumes `.page-wrapper` scrolls on wide viewports, which only holds
// while it has its own overflow. Whichever element actually scrolls is the one to read.
function getPageScroller() {
  const container = getScrollContainer();

  if (container.scrollHeight > container.clientHeight) return container;

  return document.scrollingElement ?? document.documentElement;
}

export class CollectionPlp extends HTMLElement {
  constructor() {
    super();

    this.requestId = 0;
    this.loadedCount = 0;
    this.listToken = undefined;
    this.paginationObserver = undefined;
    this.gridViewMediaQuery = window.matchMedia('(min-width: 750px)');
    const viewport = this.gridViewMediaQuery.matches ? 'desktop' : 'mobile';
    const storedGridView = sessionStorage.getItem(`collection-plp-grid-view-${viewport}`);
    this.gridView = normalizeCollectionPlpGridView(storedGridView);

    this.handlePopState = this.handlePopState.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.handleCardInteraction = this.handleCardInteraction.bind(this);
    this.handleProductClick = this.handleProductClick.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleFacetChange = this.handleFacetChange.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.handleGridViewBreakpointChange = this.handleGridViewBreakpointChange.bind(this);
    this.handleSubcollectionPointerDown = this.handleSubcollectionPointerDown.bind(this);
    this.handleSubcollectionPointerMove = this.handleSubcollectionPointerMove.bind(this);
    this.handleSubcollectionPointerUp = this.handleSubcollectionPointerUp.bind(this);
    this.handleSubcollectionClick = this.handleSubcollectionClick.bind(this);
  }

  get pageReference() {
    return this.getAttribute('page-reference');
  }

  get currencyCode() {
    return this.getAttribute('currency-code') || '';
  }

  get locale() {
    return this.getAttribute('locale') || document.documentElement.lang || undefined;
  }

  get elevateUserContext() {
    return this.getAttribute('elevate-user-context') || 'automatic';
  }

  get pageSize() {
    const value = Number(this.getAttribute('page-size'));
    return Number.isInteger(value) && value > 0 ? value : 24;
  }

  get paginationMode() {
    return this.getAttribute('pagination-mode') || 'infinite_scroll';
  }

  get paginationAlignment() {
    const value = this.getAttribute('pagination-alignment');
    return ['left', 'center', 'right'].includes(value) ? value : 'center';
  }

  get paginationShowFirstLast() {
    return this.getAttribute('pagination-show-first-last') !== 'false';
  }

  get loadMoreButtonClass() {
    const style = this.getAttribute('load-more-button-style') || 'secondary';
    const classes = ['button', 'collection-plp__load-more-button'];

    if (style !== 'primary') classes.push('button-secondary');
    if (style === 'outline') classes.push('collection-plp__load-more-button--outline');

    return classes.join(' ');
  }

  get loadMoreTextTemplate() {
    return this.getAttribute('load-more-text-template') || 'Load __count__ more';
  }

  get loadingText() {
    return this.getAttribute('loading-text') || 'Loading products…';
  }

  get tryAgainText() {
    return this.getAttribute('try-again-text') || 'Try again';
  }

  get loadErrorText() {
    return this.getAttribute('load-error-text') || 'We couldn’t load the products. Please try again.';
  }

  get progressTextTemplate() {
    return this.getAttribute('progress-text-template') || 'Showing __loaded__ of __total__ products';
  }

  get productCountTemplate() {
    return this.getAttribute('product-count-template') || '__count__ products';
  }

  updateCollectionHeaderCount(totalHits) {
    this.totalHits = totalHits;

    const targetId = this.getAttribute('product-count-target-id');
    const target = targetId ? document.getElementById(targetId) : null;
    const skeleton = targetId
      ? document.querySelector(`[data-collection-count-skeleton="${CSS.escape(targetId)}"]`)
      : null;

    if (target) {
      target.textContent = this.productCountTemplate.replace('__count__', totalHits);
      target.hidden = false;
    }

    if (skeleton) skeleton.hidden = true;

    this.dispatchEvent(
      new CustomEvent('collection-plp:product-count', {
        bubbles: true,
        detail: {
          count: totalHits,
          text: this.productCountTemplate.replace('__count__', totalHits),
        },
      }),
    );
  }

  setCollectionHeaderCountLoading(isLoading) {
    const targetId = this.getAttribute('product-count-target-id');
    const target = targetId ? document.getElementById(targetId) : null;
    const skeleton = targetId
      ? document.querySelector(`[data-collection-count-skeleton="${CSS.escape(targetId)}"]`)
      : null;

    if (target) target.hidden = true;
    if (skeleton) skeleton.hidden = !isLoading;

    this.dispatchEvent(
      new CustomEvent('collection-plp:product-count-loading', {
        bubbles: true,
        detail: { isLoading },
      }),
    );
  }

  getLabel(name, fallback) {
    return this.getAttribute(name) || fallback;
  }

  getIconUrl(name) {
    return this.getAttribute(name) || '';
  }

  getLoadMoreText(totalHits) {
    const remaining = Math.max(totalHits - this.loadedCount, 0);
    const nextBatchCount = Math.min(this.pageSize, remaining);

    return this.loadMoreTextTemplate.replace('__count__', nextBatchCount);
  }

  getProgressText(totalHits) {
    return this.progressTextTemplate
      .replace('__loaded__', Math.min(this.loadedCount, totalHits))
      .replace('__total__', totalHits);
  }

  get cardTemplate() {
    const templateId = this.getAttribute('card-template-id');
    const template = templateId ? document.getElementById(templateId) : null;

    return template instanceof HTMLTemplateElement ? template : null;
  }

  connectedCallback() {
    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('pagehide', this.handlePageHide);
    this.addEventListener('pointerdown', this.handleCardInteraction);
    this.addEventListener('click', this.handleProductClick);
    this.gridViewMediaQuery.addEventListener('change', this.handleGridViewBreakpointChange);
    this.initializeSubcollectionDragging();
    this.initialize();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('pagehide', this.handlePageHide);
    this.removeEventListener('pointerdown', this.handleCardInteraction);
    this.removeEventListener('click', this.handleProductClick);
    this.gridViewMediaQuery.removeEventListener('change', this.handleGridViewBreakpointChange);
    this.destroySubcollectionDragging();
    this.paginationObserver?.disconnect();
    this.requestId += 1;
  }

  initializeSubcollectionDragging() {
    const listId = this.getAttribute('subcollections-list-id');
    const list = listId ? document.getElementById(listId) : null;
    if (!(list instanceof HTMLElement)) return;

    this.subcollectionsList = list;
    list.addEventListener('pointerdown', this.handleSubcollectionPointerDown);
    list.addEventListener('pointermove', this.handleSubcollectionPointerMove);
    list.addEventListener('pointerup', this.handleSubcollectionPointerUp);
    list.addEventListener('pointercancel', this.handleSubcollectionPointerUp);
    list.addEventListener('click', this.handleSubcollectionClick, true);
  }

  destroySubcollectionDragging() {
    const list = this.subcollectionsList;
    if (!(list instanceof HTMLElement)) return;

    list.removeEventListener('pointerdown', this.handleSubcollectionPointerDown);
    list.removeEventListener('pointermove', this.handleSubcollectionPointerMove);
    list.removeEventListener('pointerup', this.handleSubcollectionPointerUp);
    list.removeEventListener('pointercancel', this.handleSubcollectionPointerUp);
    list.removeEventListener('click', this.handleSubcollectionClick, true);
  }

  handleSubcollectionPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.subcollectionPointerId = event.pointerId;
    this.subcollectionStartX = event.clientX;
    this.subcollectionStartScroll = this.subcollectionsList.scrollLeft;
    this.subcollectionDragged = false;
    this.subcollectionsList.setPointerCapture(event.pointerId);
    this.subcollectionsList.classList.add('is-dragging');
  }

  handleSubcollectionPointerMove(event) {
    if (event.pointerId !== this.subcollectionPointerId) return;

    const distance = event.clientX - this.subcollectionStartX;
    if (Math.abs(distance) > 3) this.subcollectionDragged = true;
    this.subcollectionsList.scrollLeft = this.subcollectionStartScroll - distance;
  }

  handleSubcollectionPointerUp(event) {
    if (event.pointerId !== this.subcollectionPointerId) return;

    this.subcollectionPointerId = undefined;
    this.subcollectionsList.classList.remove('is-dragging');
    setTimeout(() => {
      this.subcollectionDragged = false;
    });
  }

  handleSubcollectionClick(event) {
    if (!this.subcollectionDragged) return;

    event.preventDefault();
    event.stopPropagation();
    this.subcollectionDragged = false;
  }

  handleGridViewBreakpointChange() {
    const viewport = this.gridViewMediaQuery.matches ? 'desktop' : 'mobile';
    const storedGridView = sessionStorage.getItem(`collection-plp-grid-view-${viewport}`);
    this.setGridView(normalizeCollectionPlpGridView(storedGridView));
  }

  mount(panel) {
    this.replaceChildren(panel);
  }

  queryListing(options) {
    return queryCollection(options);
  }

  get resultSetKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.sort();
    return `${url.pathname}?${url.searchParams}`;
  }

  async initialize() {
    this.restoreTarget = this.readRestoreState();

    const serverSkeleton = this.querySelector(':scope > [data-server-rendered]');
    if (serverSkeleton) {
      serverSkeleton.querySelector('.collection-plp__grid')?.setAttribute('data-view', this.gridView);
      serverSkeleton.setAttribute('aria-label', this.loadingText);
    } else {
      this.renderSkeleton();
    }

    try {
      await this.refresh();
    } catch (error) {
      this.setCollectionHeaderCountLoading(false);
      this.renderError();
      console.error('[Collection PLP] Failed', error);
    }
  }

  async refresh() {
    this.setCollectionHeaderCountLoading(true);
    syncRobotsMeta();

    const restoreTarget = this.restoreTarget;
    this.restoreTarget = undefined;

    const currentRequestId = ++this.requestId;
    const usesPageNumbers = this.paginationMode === 'numbers';
    const currentPage = getSelectedPage();
    const resultSetKey = this.resultSetKey;
    const currentListToken = resultSetKey === this.activeResultSetKey ? this.listToken : undefined;

    if (resultSetKey !== this.activeResultSetKey) this.listToken = undefined;
    this.activeResultSetKey = resultSetKey;

    // With numbered pages the parameter names a single page of results. With "load more" it
    // counts the batches stacked on screen, so all of them come back together.
    const result = usesPageNumbers
      ? await this.queryListing({
          pageReference: this.pageReference,
          limit: this.pageSize,
          skip: (currentPage - 1) * this.pageSize,
          listToken: currentListToken,
          facets: getSelectedFacets(),
          sort: getSelectedSort(),
          userContext: this.elevateUserContext,
        })
      : await this.queryStackedBatches(currentPage, currentRequestId);

    if (currentRequestId !== this.requestId || !this.isConnected) return;

    const totalPages = Math.max(1, Math.ceil(result.totalHits / this.pageSize));
    if (usesPageNumbers && currentPage > totalPages) {
      history.replaceState({}, '', getPageURL(totalPages));
      await this.refresh();
      return;
    }

    this.updateCollectionHeaderCount(result.totalHits);
    this.loadedCount = result.products.length;
    this.listToken = result.listToken;
    this.render(result);

    if (!usesPageNumbers) this.syncPageURL();
    if (restoreTarget) this.restoreScrollPosition(restoreTarget);
  }

  loadedPageCount() {
    return Math.max(1, Math.ceil(this.loadedCount / this.pageSize));
  }

  /**
   * Rebuilds every batch the page parameter names, in as few requests as Elevate's limit
   * allows. Running short of the requested count — end of the list, or a hand-edited
   * parameter — stops the loop and `syncPageURL` then trims the parameter to match.
   */
  async queryStackedBatches(page, requestId) {
    const wanted = page * this.pageSize;
    const products = [];
    let response = this.listToken ? { listToken: this.listToken } : undefined;

    while (products.length < wanted) {
      const chunk = await this.queryListing({
        pageReference: this.pageReference,
        limit: Math.min(wanted - products.length, ELEVATE_MAX_LIMIT),
        skip: products.length,
        listToken: response?.listToken,
        facets: getSelectedFacets(),
        sort: getSelectedSort(),
        userContext: this.elevateUserContext,
      });

      products.push(...chunk.products);
      response = chunk;

      if (requestId !== this.requestId) break;
      if (chunk.products.length === 0 || products.length >= chunk.totalHits) break;
    }

    return { ...response, products };
  }

  /**
   * Corrects a stale `page` parameter after a full refresh (shared link, stock that moved).
   * Advancing through batches uses `updatePageURL` instead, so each "load more" is its own
   * history entry and the back button can step down one page at a time.
   */
  syncPageURL() {
    const page = this.loadedPageCount();

    if (page === getSelectedPage()) return;

    replacePageURL(page);
  }

  /**
   * The grid is built after the Elevate response, too late for the browser to land on the
   * card the buyer opened. The reached position is stored for every page of the collection,
   * including the first, and replayed once the grid is on screen again.
   */
  get restoreStorageKey() {
    const url = new URL(window.location.href);
    const parameters = new URLSearchParams(url.search);

    parameters.delete('page');
    parameters.sort();

    const query = parameters.toString();

    return `${RESTORE_STORAGE_PREFIX}:${url.pathname}${query ? `?${query}` : ''}`;
  }

  handleCardInteraction(event) {
    const card = event.target instanceof Element ? event.target.closest('[data-product-key]') : null;

    this.lastTouchedProductKey = card?.getAttribute('data-product-key') || undefined;
  }

  handleProductClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a') : null;
    if (link?.hasAttribute('data-brand-link')) return;

    const productCard = link?.closest('[data-product-key]');
    if (productCard) this.lastTouchedProductKey = productCard.getAttribute('data-product-key') || undefined;

    const card = link?.closest('[data-card]');
    const ticket = link?.dataset.elevateTicket || card?.dataset.collectionTicket;

    if (!ticket || resolveUserContext(this.elevateUserContext) === 'ANONYMOUS') return;

    getElevateClient()
      .then((client) => client.notify.click(ticket))
      .catch(() => {});
  }

  handlePageHide() {
    if (this.paginationMode === 'numbers') return;

    try {
      sessionStorage.setItem(
        this.restoreStorageKey,
        JSON.stringify({
          productKey: this.lastTouchedProductKey,
          scrollTop: getPageScroller().scrollTop,
        }),
      );
    } catch (error) {
      console.error('[Collection PLP] Failed to store scroll position', error);
    }
  }

  readRestoreState() {
    if (this.paginationMode === 'numbers') return undefined;

    try {
      // Read on every load, not only the ones that use it, so a fresh visit to the collection
      // drops the stale entry instead of replaying it on some later back navigation.
      const stored = sessionStorage.getItem(this.restoreStorageKey);
      sessionStorage.removeItem(this.restoreStorageKey);

      const [navigation] = performance.getEntriesByType('navigation');
      if (!stored || !['back_forward', 'reload'].includes(navigation?.type)) return undefined;

      // Coming back from a product means returning to that card. A reload means staying put,
      // whatever the buyer last touched before refreshing.
      return { ...JSON.parse(stored), anchorToProduct: navigation?.type === 'back_forward' };
    } catch (error) {
      console.error('[Collection PLP] Failed to read scroll position', error);
      return undefined;
    }
  }

  restoreScrollPosition({ productKey, scrollTop, anchorToProduct }) {
    requestAnimationFrame(() => {
      const card =
        anchorToProduct && productKey ? this.querySelector(`[data-product-key="${CSS.escape(productKey)}"]`) : null;

      if (card) {
        card.scrollIntoView({ block: 'center', behavior: 'instant' });
        return;
      }

      if (Number.isFinite(scrollTop)) getPageScroller().scrollTo({ top: scrollTop, behavior: 'instant' });
    });
  }

  async handleSortChange(event) {
    const control = event.target;
    if (!(control instanceof HTMLSelectElement) && !(control instanceof HTMLInputElement)) return;

    updateSortURL(control.value);

    try {
      await this.refresh();
    } catch (error) {
      this.setCollectionHeaderCountLoading(false);
      console.error('[Collection PLP] Failed to update sorting', error);
    }
  }

  async handleFacetChange(event) {
    const input = event.target;
    const form = event.currentTarget;

    if (!(input instanceof HTMLInputElement)) return;
    if (!(form instanceof HTMLFormElement)) return;
    if (input.type !== 'checkbox') return;

    const selectedValues = [...form.querySelectorAll(`input[name="${CSS.escape(input.name)}"]:checked`)].map(
      (selectedInput) => selectedInput.value,
    );

    updateFacetURL(input.name, selectedValues);

    try {
      await this.refresh();
    } catch (error) {
      this.setCollectionHeaderCountLoading(false);
      console.error('[Collection PLP] Failed to update facet', error);
    }
  }

  async handleFacetSubmit(event, facets) {
    event.preventDefault();

    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    const drawer = form.closest('theme-drawer');

    for (const facet of facets) {
      if (facet.type !== 'RANGE') continue;

      const min = form.elements.namedItem(`${facet.id}.min`);
      const max = form.elements.namedItem(`${facet.id}.max`);

      updateRangeFacetURL(
        facet.id,
        min instanceof HTMLInputElement ? min.value : '',
        max instanceof HTMLInputElement ? max.value : '',
      );
    }

    try {
      await this.refresh();
      drawer?.close?.();
    } catch (error) {
      this.setCollectionHeaderCountLoading(false);
      console.error('[Collection PLP] Failed to apply range facets', error);
    }
  }

  async handleLoadMore(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const wrapper = button.closest('.collection-plp__load-more');
    button.disabled = true;
    button.textContent = this.loadingText;

    const currentRequestId = ++this.requestId;

    try {
      const result = await this.queryListing({
        pageReference: this.pageReference,
        limit: this.pageSize,
        skip: this.loadedCount,
        listToken: this.listToken,
        facets: getSelectedFacets(),
        sort: getSelectedSort(),
        userContext: this.elevateUserContext,
      });

      if (currentRequestId !== this.requestId || !this.isConnected) return;

      const grid = this.querySelector('.collection-plp__grid');
      if (!(grid instanceof HTMLUListElement)) return;

      grid.append(...result.products.map((product) => this.createProductCard(product)));
      this.loadedCount += result.products.length;
      this.listToken = result.listToken ?? this.listToken;
      this.updatePaginationControl(wrapper, result.totalHits);

      const page = this.loadedPageCount();
      if (page !== getSelectedPage()) updatePageURL(page);

      if (this.loadedCount >= result.totalHits || result.products.length === 0) {
        this.paginationObserver?.disconnect();
        wrapper?.classList.add('collection-plp__load-more--complete');
        button.remove();
        return;
      }

      button.disabled = false;
      button.textContent = this.getLoadMoreText(result.totalHits);

      if (this.paginationMode === 'infinite_scroll' && wrapper?.isConnected) {
        this.paginationObserver?.observe(wrapper);
      }
    } catch (error) {
      button.disabled = false;
      button.textContent = this.tryAgainText;
      wrapper?.classList.remove('collection-plp__load-more--automatic');
      console.error('[Collection PLP] Failed to load more products', error);
    }
  }

  handlePopState() {
    this.restoreTarget = {
      scrollTop: history.state?.scrollTop,
      anchorToProduct: false,
    };

    this.refresh().catch((error) => {
      this.setCollectionHeaderCountLoading(false);
      this.renderError();
      console.error('[Collection PLP] Failed to restore URL state', error);
    });
  }
}

Object.assign(CollectionPlp.prototype, collectionPlpViewMethods);

if (!customElements.get('collection-plp')) {
  customElements.define('collection-plp', CollectionPlp);
}
